import React from 'react';
import { WandSparkles } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Field } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { AutoGrowTextarea } from '@/components/ui/auto-grow-textarea';
import type { DRBFMInputPattern } from '../../../types/drbfm';

interface InputArtifactPanelProps {
  inputPattern: DRBFMInputPattern;
  oldText: string;
  newText: string;
  changeReportText: string;
  oldCadModelId: string;
  newCadModelId: string;
  isLoading: boolean;
  onOldTextChange: (value: string) => void;
  onNewTextChange: (value: string) => void;
  onChangeReportTextChange: (value: string) => void;
  onOldCadModelIdChange: (value: string) => void;
  onNewCadModelIdChange: (value: string) => void;
  onExtractChanges: () => void | Promise<void>;
}

const TEXTAREA = 'revy-cjk text-sm leading-[1.7]';

const InputArtifactPanel: React.FC<InputArtifactPanelProps> = ({
  inputPattern,
  oldText,
  newText,
  changeReportText,
  oldCadModelId,
  newCadModelId,
  isLoading,
  onOldTextChange,
  onNewTextChange,
  onChangeReportTextChange,
  onOldCadModelIdChange,
  onNewCadModelIdChange,
  onExtractChanges,
}) => {
  const { t } = useTranslation();

  return (
  <div className="flex flex-col gap-4">
    <div>
      <h2 className="revy-cjk text-base font-medium leading-[1.6]">{t('drbfm.workflow.input.title', '入力成果物')}</h2>
      <p className="revy-cjk text-sm leading-[1.6] text-muted-foreground">
        {t('drbfm.workflow.input.description', 'この工程で使う旧新情報や変化点資料を登録します。')}
      </p>
    </div>

    {inputPattern === 'change_report' ? (
      <Field label={t('drbfm.workflow.input.changeReportLabel', '変化点資料')}>
        {(control) => (
          <AutoGrowTextarea
            {...control}
            rows={8}
            className={TEXTAREA}
            value={changeReportText}
            disabled={isLoading}
            onChange={(event) => onChangeReportTextChange(event.target.value)}
          />
        )}
      </Field>
    ) : inputPattern === 'old_new_cad' ? (
      <div className="flex flex-col gap-4 min-[900px]:flex-row">
        <Field label={t('drbfm.workflow.input.oldCadModelIdLabel', '旧CADモデルID')} className="min-w-0 flex-1">
          {(control) => (
            <Input
              {...control}
              value={oldCadModelId}
              disabled={isLoading}
              onChange={(event) => onOldCadModelIdChange(event.target.value)}
            />
          )}
        </Field>
        <Field label={t('drbfm.workflow.input.newCadModelIdLabel', '新CADモデルID')} className="min-w-0 flex-1">
          {(control) => (
            <Input
              {...control}
              value={newCadModelId}
              disabled={isLoading}
              onChange={(event) => onNewCadModelIdChange(event.target.value)}
            />
          )}
        </Field>
      </div>
    ) : (
      <div className="flex flex-col gap-4 min-[900px]:flex-row">
        <Field
          label={
            inputPattern === 'old_new_drawings'
              ? t('drbfm.workflow.input.oldDrawingExtractedText', '旧図面の抽出テキスト')
              : t('drbfm.workflow.input.oldSpecLabel', '旧仕様')
          }
          className="min-w-0 flex-1"
        >
          {(control) => (
            <AutoGrowTextarea
              {...control}
              rows={8}
              className={TEXTAREA}
              value={oldText}
              disabled={isLoading}
              onChange={(event) => onOldTextChange(event.target.value)}
            />
          )}
        </Field>
        <Field
          label={
            inputPattern === 'old_new_drawings'
              ? t('drbfm.workflow.input.newDrawingExtractedText', '新図面の抽出テキスト')
              : t('drbfm.workflow.input.newSpecLabel', '新仕様')
          }
          className="min-w-0 flex-1"
        >
          {(control) => (
            <AutoGrowTextarea
              {...control}
              rows={8}
              className={TEXTAREA}
              value={newText}
              disabled={isLoading}
              onChange={(event) => onNewTextChange(event.target.value)}
            />
          )}
        </Field>
      </div>
    )}

    <div className="flex justify-end">
      <Button disabled={isLoading} onClick={onExtractChanges}>
        <WandSparkles strokeWidth={1.9} />
        <span className="revy-cjk">{t('drbfm.workflow.input.extractChanges', '変更点を抽出')}</span>
      </Button>
    </div>
  </div>
  );
};

export default InputArtifactPanel;
